import React from 'react';
import PropTypes from 'prop-types';
import TextInput from 'components/text-input-view';
import SelectInput from 'components/select-input-view';
import AvatarSelectorContainer from '../containers/avatar-selector-container';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import st from 'core/shared-translations';
import classes from './personal-info.scss';
import ReactTooltip from 'react-tooltip';

const t = defineMessages({
  'more-about-you-section.title': {
    id: 'more-about-you-section.title',
    defaultMessage: 'More About You',
    description: 'Section title for the more about you fields',
  },
  'personal-info.name': {
    id: 'personal-info.name',
    defaultMessage: 'Name (required)',
    description: 'Field label for user name input',
  },
  'personal-info.timezone': {
    id: 'personal-info.timezone',
    defaultMessage: 'Time zone',
    description: 'Field label for time zone selection',
  },
  'personal-info.language': {
    id: 'personal-info.language',
    defaultMessage: 'Preferred language',
    description: 'Field label for language selection',
  },
  'personal-info.personal-info-help-message': {
    id: 'personal-info.personal-info-help-message',
    defaultMessage: 'Your personal info sets the name, language, dates, and times you see in LogMeIn products.',
    description: 'Message to be displayed in the popover dialog when the Personal Info help icon is clicked.',
  },
  'personal-info.about-you-help-message': {
    id: 'personal-info.about-you-help-message',
    defaultMessage: '"About you" info can appear in product features like your personal meeting page.',
    description: 'Message to be displayed in the popover dialog when the More About You help icon is clicked.',
  },
  'personal-info.validation-error-field-name': {
    id: 'personal-info.validation-error-field-name',
    defaultMessage: 'Enter your name.',
    description: 'Content of the error message when the Preferred Name field is left blank',
  },
  'personal-info.name-exceeds-max-length': {
    id: 'personal-info.name-exceeds-max-length',
    defaultMessage: 'You cannot enter more than 128 characters.',
    description: 'Content of the error message when the entry in the Name field exceeds more than 128 characters',
  },
  'personal-info.title': {
    id: 'personal-info.title',
    defaultMessage: 'Title',
    description: 'Field label for the user\'s title field',
  },
  'personal-info.title-exceeds-max-length': {
    id: 'personal-info.title-exceeds-max-length',
    defaultMessage: 'Enter a title less than 250 characters.',
    description: 'Content of the error message when the entry in the Title field exceeds more than 250 characters',
  },
  'personal-info.profileUrl': {
    id: 'personal-info.profileUrl',
    defaultMessage: 'Link to profile or business',
    description: 'Field label for the user\'s about URL field',
  },
  'personal-info.profileUrl-malformed-url': {
    id: 'personal-info.profileUrl-malformed-url',
    defaultMessage: 'Enter a valid URL less than 250 characters starting with http:// or https://',
    description: 'Content of the error message when the entry in the Name field exceeds more than 250 characters',
  },
  'personal-info.location': {
    id: 'personal-info.location',
    defaultMessage: 'Location',
    description: 'Field label for the user\'s location field',
  },
  'personal-info.location-exceeds-max-length': {
    id: 'personal-info.location-exceeds-max-length',
    defaultMessage: 'Enter a location less than 250 characters.',
    description: 'Content of the error message when the entry in the Location field exceeds more than 250 characters',
  },
});

const PersonalInfo = ({
  intl,
  personData,
  allLocales,
  allTimeZones,
  errors,
  onChange,
  onBlur,
  onLocaleChange,
  onTimeZoneChange,
  onSave,
  onCancel,
  enableCTAs,
  isLoading,
  locationValue,
}) => (
  <div>
    <AvatarSelectorContainer />
    <div id="personal-info-section" className={classes['personalInfoContainer']}>
      <form style={{marginTop: '0px'}}>
        <div className="row">
          <div id="personal-info-section-title" className={`col-xs-12 sectionTitle ${classes['personalInfoSectionTitle']}`}>
            <FormattedMessage {...st['shared.personal.info']} />&nbsp;
            <i className="togo-icon togo-icon-question-circle togo-icon-question-circle-leftpadding help-icon-move-down" data-tip data-for="personalInfo_tip" />
            <ReactTooltip
              id="personalInfo_tip"
              className="helpToolTip"
              type="light"
              place="bottom"
              effect="solid"
              border
              multiline>
              <span>{intl.formatMessage(t['personal-info.personal-info-help-message'])}</span>
            </ReactTooltip>
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12">
            <TextInput
              name="displayName"
              label={intl.formatMessage(t['personal-info.name'])}
              helpText=""
              value={personData.displayName}
              onChange={onChange}
              onBlur={onBlur}
              isShallowGroup
              error={
                errors.displayName &&
                intl.formatMessage(st[errors.displayName] || t[errors.displayName])
              }
            />
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12 col-sm-6">
            <SelectInput
              name="locale"
              label={intl.formatMessage(t['personal-info.language'])}
              // helpText={intl.formatMessage(t['personal-info.language-and-timezone-help-message'])}
              value={personData.selectedLocale}
              defaultOption=""
              options={allLocales}
              onChange={onLocaleChange}
              isShallowGroup
              error={errors.locale}
              // placeholder={intl.formatMessage(st['shared.select.prompt'])}
            />
          </div>
          <div className="col-xs-12 col-sm-6">
            <SelectInput
              name="timezone"
              label={intl.formatMessage(t['personal-info.timezone'])}
              value={personData.timezone}
              options={allTimeZones}
              onChange={onTimeZoneChange}
              isShallowGroup
              error={errors.timezone}
            />
          </div>
        </div>
        <div className="row">
          <div id="more-about-you-section-title" className={`col-xs-12 sectionTitle ${classes['personalInfoSectionTitle']} ${classes['midPage']}`}>
            <FormattedMessage {...t['more-about-you-section.title']} />&nbsp;
            <i className="togo-icon togo-icon-question-circle togo-icon-question-circle-leftpadding help-icon-move-down" data-tip data-for="aboutYou_tip" />
            <ReactTooltip
              id="aboutYou_tip"
              className="helpToolTip"
              type="light"
              place="bottom"
              effect="solid"
              border
              multiline>
              <span>{intl.formatMessage(t['personal-info.about-you-help-message'])}</span>
            </ReactTooltip>
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12 col-sm-6">
            <TextInput
              name="title"
              label={intl.formatMessage(t['personal-info.title'])}
              helpText=""
              value={personData.title}
              onChange={onChange}
              onBlur={onBlur}
              isShallowGroup
              error={
                errors.title &&
                intl.formatMessage(st[errors.title] || t[errors.title])
              }
            />
          </div>
          <div className="col-xs-12 col-sm-6">
            <TextInput
              name="location"
              label={intl.formatMessage(t['personal-info.location'])}
              helpText=""
              value={locationValue}
              onChange={onChange}
              onBlur={onBlur}
              isShallowGroup
              error={
                errors.location &&
                intl.formatMessage(st[errors.location] || t[errors.location])
              }
            />
          </div>
        </div>
        <div className="row">
          <div className="col-xs-12">
            <TextInput
              name="profileUrl"
              label={intl.formatMessage(t['personal-info.profileUrl'])}
              helpText=""
              value={personData.profileUrl}
              onChange={onChange}
              onBlur={onBlur}
              isShallowGroup
              error={
                errors.profileUrl &&
                intl.formatMessage(st[errors.profileUrl] || t[errors.profileUrl])
              }
            />
          </div>
        </div>
      </form>
      <div className={`row ${classes['buttonRow']}`}>
        <div className="col-xs-12 col-sm-6 no-gutter">
          <button
            id="user-personal-info-save"
            type="submit"
            disabled={!enableCTAs}
            className="btn btn-default ninety-button marginl-5"
            onClick={onSave}
          >
            {isLoading ? <FormattedMessage {...st['shared.saving']} /> : <FormattedMessage {...st['shared.save']} />}
          </button>
        </div>
        <div className="col-xs-12 col-sm-6 no-gutter">
          <button
            id="user-personal-info-cancel"
            type="button"
            className="btn btn-tertiary ninety-button"
            disabled={!enableCTAs}
            onClick={onCancel}
          >
            <FormattedMessage {...st['shared.cancel']} />
          </button>
        </div>
      </div>
    </div>
  </div>
);

PersonalInfo.propTypes = {
  intl: PropTypes.object.isRequired,
  personData: PropTypes.object.isRequired,
  allLocales: PropTypes.array.isRequired,
  allTimeZones: PropTypes.array.isRequired,
  errors: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onLocaleChange: PropTypes.func.isRequired,
  onTimeZoneChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  enableCTAs: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  locationValue: PropTypes.string,
};

export default injectIntl(PersonalInfo);
